import { Directory, Filesystem } from "@capacitor/filesystem";
import { FileOpener } from "@capawesome-team/capacitor-file-opener";
import { Cubit } from "blac";
import { Blac } from "blac-next";
import appErrors from "src/constants/appErrors";
import type FileType from "src/constants/fileType";
import { globalEvents } from "src/constants/globalEvents";
import { DateFormats, dateLocal } from "src/lib/date";
import envVariables from "src/lib/envVariables";
import {
  cacheDownloadedFile,
  DbFileService,
  readCachedBlob
} from "src/lib/db/DbFileService";
import { fileTransferConfig } from "src/lib/fileTransferConfig";
import mimeType from "src/lib/mimeType";
import {
  isFlutterInAppWebView,
  sendFileToFlutterApp
} from "src/lib/flutterFileBridge";
import { isHybridApp, isIOS } from "src/lib/platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import {
  authenticationState,
  fileState,
  toast,
  tracker
} from "src/state/state";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import type { FileViewContext } from "src/ui/components/PDFViewer/PDFViewerBloc";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import PDFViewerBloc from "src/ui/components/PDFViewer/PDFViewerBloc";

import {
  FileControllerService,
  UserShortLivedTokenControllerService
} from "@9amhealth/openapi";
import {
  blobToBase64,
  downloadFileWithProgress,
  fetchFileHeaders,
  type FileHeaderMetadata,
  formatBytes,
  parseFileHeaders,
  sanitizeFileName,
  triggerBlobDownload,
  uploadFileWithProgress
} from "@9amhealth/shared";
import translate from "src/lib/translate";
import imageProbeSize from "src/lib/imageProbeSize";

const PDFViewerController = Blac.getBloc(PDFViewerBloc);

const PDF_MIME_TYPE = "application/pdf";
const ERROR_OPEN_FILE_FAILED = "error.openFileFailed";
const ERROR_DOWNLOAD_FILE = "error.downloadFile";

interface FileAttributes {
  "file.name"?: string;
  "file.%s.name"?: string;
  "image.width"?: number;
  "image.height"?: number;
  "subscription.identity.face"?: boolean;
  "subscription.identity.id"?: boolean;
  "labs.user_result"?: boolean;
  "file.tags"?: string[];
  "file.lastModified"?: string;
  "file.size"?: number;
  "file.type"?: FileType;
  "source.name"?: string;
}

export interface FileItem {
  id: string;
  attributes: FileAttributes;
  type: string;
  blob?: Blob;
  path?: string;
  size?: number;
  error?: string;
  local?: boolean;
}

export type { FileHeaderMetadata };

export default class FilesCubit extends Cubit<FileItem[]> {
  constructor() {
    super([]);

    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit([]);
      void DbFileService.clearAll();
    });
  }

  public readonly getFileById = (id: string): FileItem | undefined => {
    return this.state.find((item) => item.id === id);
  };

  public readonly getFileByAttribute = (
    attribute: FileAttributes
  ): FileItem[] => {
    return this.state.filter((item) => {
      const attributeName = Object.keys(attribute)[0] as never;
      return (
        Object.keys(item.attributes).includes(attributeName) &&
        item.attributes[attributeName] === attribute[attributeName]
      );
    });
  };

  private readonly attachImageDimensions = async (
    attributes: FileAttributes,
    filePath: string,
    fileType: string
  ): Promise<void> => {
    if (mimeType.isImage(fileType)) {
      try {
        const { width: imageWidth, height: imageHeight } =
          await imageProbeSize(filePath);
        attributes["image.width"] = imageWidth;
        attributes["image.height"] = imageHeight;
      } catch (e) {
        reportErrorSentry(e);
      }
    }
  };

  public readonly uploadFile = async (options: {
    file: File;
    fileAttributes?: FileAttributes;
  }): Promise<FileItem | undefined> => {
    try {
      const { file, fileAttributes } = options;

      const attributes: FileAttributes = {
        "source.name": envVariables.APP_ENV
      };

      const name = sanitizeFileName(file.name);

      attributes["file.name"] = name;
      attributes["file.lastModified"] = dateLocal(file.lastModified).format(
        DateFormats.ISO_FULL
      );
      attributes["file.size"] = file.size;
      const filePath = URL.createObjectURL(file);

      await this.attachImageDimensions(attributes, filePath, file.type);

      const allAttributes = {
        ...attributes,
        ...fileAttributes
      };

      const fileAttributesHeader = JSON.stringify({
        "file.name": allAttributes["file.name"],
        "file.type": fileAttributes?.["file.type"],
        ...(allAttributes["image.width"] && {
          "image.width": String(allAttributes["image.width"])
        }),
        ...(allAttributes["image.height"] && {
          "image.height": String(allAttributes["image.height"])
        })
      });

      const response = await FileControllerService.createFile(
        file,
        fileAttributesHeader
      );

      const item: FileItem = {
        id: response.data.fileId,
        attributes: allAttributes,
        local: true,
        type: file.type,
        path: filePath
      };

      this.emit([...this.state, item]);
      return item;
    } catch (e: unknown) {
      toast.error("error_upload_file_failed");
      reportErrorSentry(e);
      return undefined;
    }
  };

  public readonly uploadFileWithProgress = async (
    options: {
      file: File;
      fileAttributes?: FileAttributes;
    },
    showProgress = true,
    customCallbacks?: {
      onProgress?: (loaded: number, total: number, percentage: number) => void;
    }
  ): Promise<FileItem | undefined> => {
    const { file, fileAttributes } = options;
    const abortController = new AbortController();

    try {
      const attributes: FileAttributes = {
        "source.name": envVariables.APP_ENV
      };

      const name = sanitizeFileName(file.name);

      attributes["file.name"] = name;
      attributes["file.lastModified"] = dateLocal(file.lastModified).format(
        DateFormats.ISO_FULL
      );
      attributes["file.size"] = file.size;
      const filePath = URL.createObjectURL(file);

      await this.attachImageDimensions(attributes, filePath, file.type);

      const allAttributes = {
        ...attributes,
        ...fileAttributes
      };

      const fileAttributesHeader = JSON.stringify({
        "file.name": allAttributes["file.name"],
        "file.type": fileAttributes?.["file.type"],
        ...(allAttributes["image.width"] && {
          "image.width": String(allAttributes["image.width"])
        }),
        ...(allAttributes["image.height"] && {
          "image.height": String(allAttributes["image.height"])
        })
      });

      if (showProgress) {
        BlockingLoadingOverlayController.startLoadingWithProgress({
          title: "upload.uploading",
          message: "upload.pleaseWait",
          bg: "transparent",
          onCancel: () => {
            abortController.abort();
            BlockingLoadingOverlayController.endLoading();
          }
        });
      }

      const handleProgress = (
        loaded: number,
        total: number,
        percentage: number
      ): void => {
        customCallbacks?.onProgress?.(loaded, total, percentage);
        if (showProgress) {
          const label = `${formatBytes(loaded)} / ${formatBytes(total)}`;
          BlockingLoadingOverlayController.updateProgress(percentage, label);
        }
      };

      const response = await uploadFileWithProgress(
        file,
        fileTransferConfig,
        fileAttributesHeader,
        {
          signal: abortController.signal,
          onProgress: handleProgress
        }
      );

      const item: FileItem = {
        id: response.fileId,
        attributes: allAttributes,
        local: true,
        type: file.type,
        path: filePath
      };

      this.emit([...this.state, item]);

      if (showProgress) {
        BlockingLoadingOverlayController.loadingSuccess({
          title: "upload.complete"
        });

        setTimeout(() => {
          BlockingLoadingOverlayController.endLoading();
        }, 1500);
      }

      return item;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        if (showProgress) {
          BlockingLoadingOverlayController.endLoading();
        }
        return undefined;
      }

      reportErrorSentry(error);
      toast.error("error_upload_file_failed");

      if (showProgress) {
        BlockingLoadingOverlayController.loadingError({
          title: "upload.failed",
          message: "error.uploadFile",
          retry: () => {
            void this.uploadFileWithProgress(options, showProgress);
          }
        });
      }
      return undefined;
    }
  };

  static requestDownloadFile = async (id: string): Promise<Response> =>
    fetch(`${envVariables.API_BASE_URL}/v1/files/${id}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authenticationState.accessToken}`
      }
    });

  hasFileMetadata = async (id: string): Promise<boolean> => {
    const cached = await DbFileService.getFileMetadata(id);
    return cached !== null;
  };

  getFileMetadata = async (id: string): Promise<FileHeaderMetadata | null> => {
    const cached = await DbFileService.getFileMetadata(id);
    if (cached) {
      return {
        contentType: cached.contentType,
        contentLength: cached.contentLength,
        filename: cached.filename,
        fileType: cached.fileType
      };
    }

    const metadata = await fetchFileHeaders(id, fileTransferConfig);
    if (metadata) {
      await DbFileService.putFileMetadata(id, metadata);
    }
    return metadata;
  };

  getFileContentType = async (id: string): Promise<string | null> => {
    const metadata = await this.getFileMetadata(id);
    return metadata?.contentType ?? null;
  };

  static requestDownloadFilePublicUrl = async (id: string): Promise<string> => {
    const shortLived =
      await UserShortLivedTokenControllerService.retrieveShortLivedToken();
    const { accessToken } = shortLived.data;
    return `${envVariables.API_BASE_URL}/v1/files/${id}?authToken=${accessToken}`;
  };

  static requestDownloadFilePreview = async (
    id: string,
    w: number,
    h: number
  ): Promise<Response> =>
    fetch(`${envVariables.API_BASE_URL}/v1/files/${id}/preview/${w}/${h}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authenticationState.accessToken}`
      }
    });

  public readonly addFile = (id: string, fileBlob: Blob): FileItem => {
    const file: FileItem = {
      id,
      attributes: {},
      type: ""
    };

    try {
      file.path = URL.createObjectURL(fileBlob);
      this.emit([...this.state, file]);
    } catch (e: unknown) {
      // eslint-disable-next-line no-console
      console.error(e);
      return { ...file, error: appErrors.generic };
    }

    return file;
  };

  static startFileDownload = async (
    id: string,
    source?: string
  ): Promise<void> => {
    try {
      BlockingLoadingOverlayController.startLoading({
        title: "download.downloading",
        message: "download.pleaseWait",
        bg: "transparent"
      });

      const metadata = await fileState.getFileMetadata(id);
      const isPdf = metadata?.contentType === PDF_MIME_TYPE;

      BlockingLoadingOverlayController.endLoading();

      if (metadata && isPdf) {
        await FilesCubit.downloadAndOpenPdf(id, source, metadata);
        return;
      }

      await FilesCubit.openFileViewerPopup(id, source, metadata);
    } catch (error) {
      BlockingLoadingOverlayController.endLoading();
      reportErrorSentry(error, { fileId: id, source });
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.fail,
        data: {
          fileId: id,
          source,
          failurePoint: "download",
          errorMessage: error instanceof Error ? error.message : String(error)
        }
      });
      toast.error(ERROR_DOWNLOAD_FILE);
    }
  };

  static downloadAndOpenPdf = async (
    id: string,
    source: string | undefined,
    metadata: FileHeaderMetadata
  ): Promise<void> => {
    tracker.track(TrackEvent.FileViewer, {
      type: TrackType.open,
      data: {
        fileId: id,
        fileType: metadata.contentType ?? PDF_MIME_TYPE,
        fileSize: metadata.contentLength ?? "0",
        source,
        stage: "download"
      }
    });

    const downloadResult = await FilesCubit.downloadFileWithOverlay(id);
    if (!downloadResult) {
      return;
    }

    const filename =
      downloadResult.metadata?.filename ?? metadata.filename ?? `file_${id}`;

    tracker.track(TrackEvent.FileViewer, {
      type: TrackType.complete,
      data: {
        fileId: id,
        fileName: filename,
        fileType: metadata.contentType ?? PDF_MIME_TYPE,
        fileSize:
          downloadResult.metadata?.contentLength ??
          metadata.contentLength ??
          "0",
        source,
        wasCached: downloadResult.wasCached,
        stage: "download"
      }
    });

    const context: FileViewContext = {
      fileId: id,
      contentType: metadata.contentType ?? undefined,
      contentLength:
        downloadResult.metadata?.contentLength ??
        metadata.contentLength ??
        undefined,
      source,
      wasCached: downloadResult.wasCached
    };

    await FilesCubit.openFilePreview(
      downloadResult.blob,
      filename,
      downloadResult.cachedUri,
      context
    );
  };

  static openFileViewerPopup = async (
    id: string,
    source: string | undefined,
    metadata: FileHeaderMetadata | null
  ): Promise<void> => {
    tracker.track(TrackEvent.FileViewer, {
      type: TrackType.open,
      data: {
        fileId: id,
        fileType: metadata?.contentType ?? undefined,
        source
      }
    });

    const { FileViewerPopupController } =
      await import("src/ui/components/FileViewerPopup/FileViewerPopupBloc");
    await FileViewerPopupController.open(id);
  };

  static downloadFileWithOverlay = async (
    id: string
  ): Promise<{
    blob: Blob;
    wasCached: boolean;
    metadata: FileHeaderMetadata | null;
    cachedUri?: string;
  } | null> => {
    const cachedEntry = await DbFileService.getFile(id);
    if (cachedEntry) {
      const blob = await readCachedBlob(cachedEntry);
      if (blob) {
        return {
          blob,
          wasCached: true,
          metadata: null,
          cachedUri: cachedEntry.nativeUri
        };
      }
      await DbFileService.removeFile(id);
    }

    const abortController = new AbortController();
    let metadata: FileHeaderMetadata | null = null;

    try {
      BlockingLoadingOverlayController.startLoadingWithProgress({
        title: "download.downloading",
        message: "download.pleaseWait",
        bg: "transparent",
        onCancel: () => {
          abortController.abort();
          BlockingLoadingOverlayController.endLoading();
        }
      });

      const blob = await downloadFileWithProgress(id, fileTransferConfig, {
        signal: abortController.signal,
        onHeaders: (headers) => {
          metadata = parseFileHeaders(headers);
        },
        onProgress: (loaded, total, percentage) => {
          const label =
            total > 0
              ? `${formatBytes(loaded)} / ${formatBytes(total)}`
              : formatBytes(loaded);

          BlockingLoadingOverlayController.updateProgress(percentage, label);
        }
      });

      BlockingLoadingOverlayController.endLoading();

      const filename =
        (metadata as FileHeaderMetadata | null)?.filename ?? `file_${id}`;
      const fileMetadata = metadata as FileHeaderMetadata | null;
      const cached = await cacheDownloadedFile(
        id,
        blob,
        filename,
        blob.type,
        fileMetadata
          ? {
              contentType: fileMetadata.contentType,
              contentLength: fileMetadata.contentLength,
              fileType: fileMetadata.fileType
            }
          : undefined
      );

      return { blob, wasCached: false, metadata, cachedUri: cached.nativeUri };
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        BlockingLoadingOverlayController.endLoading();
        return null;
      }

      reportErrorSentry(error, { fileId: id });

      return new Promise((resolve) => {
        BlockingLoadingOverlayController.loadingError({
          title: "download.failed",
          message: ERROR_DOWNLOAD_FILE,
          retry: () => {
            FilesCubit.downloadFileWithOverlay(id)
              .then(resolve)
              .catch(() => resolve(null));
          },
          onCancel: () => resolve(null)
        });
      });
    }
  };

  static checkFileExists = async (uri: string): Promise<boolean> => {
    try {
      await Filesystem.stat({ path: uri });
      return true;
    } catch {
      return false;
    }
  };

  static openFileNative = async (
    uri: string,
    fileMimeType: string
  ): Promise<void> => {
    try {
      await FileOpener.openFile({
        path: uri,
        mimeType: fileMimeType || "application/octet-stream"
      });
    } catch (error) {
      reportErrorSentry(error, { uri, mimeType: fileMimeType });
      toast.error(ERROR_OPEN_FILE_FAILED);
    }
  };

  static openFilePreview = async (
    blob: Blob,
    filename: string,
    cachedUri?: string,
    context?: FileViewContext
  ): Promise<void> => {
    try {
      const isPDF =
        blob.type === PDF_MIME_TYPE || filename.toLowerCase().endsWith(".pdf");

      if (isPDF) {
        if (isHybridApp()) {
          tracker.track(TrackEvent.FileViewer, {
            type: TrackType.open,
            data: {
              fileId: context?.fileId,
              fileName: filename,
              fileType: context?.contentType ?? PDF_MIME_TYPE,
              fileSize: context?.contentLength,
              source: context?.source,
              wasCached: context?.wasCached,
              stage: "open-pdf"
            }
          });
          await FilesCubit.openPDFNative(blob, filename, cachedUri, context);
        } else {
          const blobUrl = URL.createObjectURL(blob);
          PDFViewerController.open(blobUrl, filename, context);
        }
      } else if (isHybridApp() && cachedUri) {
        tracker.track(TrackEvent.FileViewer, {
          type: TrackType.open,
          data: {
            fileId: context?.fileId,
            fileName: filename,
            fileType: blob.type,
            fileSize: context?.contentLength,
            source: context?.source,
            wasCached: context?.wasCached
          }
        });
        await FilesCubit.openFileNative(cachedUri, blob.type);
      } else if (!isHybridApp()) {
        tracker.track(TrackEvent.FileViewer, {
          type: TrackType.open,
          data: {
            fileId: context?.fileId,
            fileName: filename,
            fileType: blob.type,
            fileSize: context?.contentLength,
            source: context?.source,
            wasCached: context?.wasCached
          }
        });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      }
    } catch (error) {
      reportErrorSentry(error, {
        fileName: filename,
        fileType: blob.type,
        ...context
      });
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.fail,
        data: {
          fileId: context?.fileId,
          fileName: filename,
          fileType: blob.type,
          source: context?.source,
          failurePoint: "open",
          errorMessage: error instanceof Error ? error.message : String(error)
        }
      });
      toast.error(ERROR_OPEN_FILE_FAILED);
    }
  };

  static downloadFile = async (blob: Blob, filename: string): Promise<void> => {
    if (isFlutterInAppWebView()) {
      try {
        const sent = await sendFileToFlutterApp(blob, filename);
        if (!sent) {
          reportErrorSentry(
            new Error("Failed to send file to Flutter app - Silent")
          );
          toast.error(ERROR_DOWNLOAD_FILE);
        }
      } catch (error) {
        reportErrorSentry(
          new Error("Error sending file to Flutter app", { cause: error })
        );
        toast.error(ERROR_DOWNLOAD_FILE);
      }
      return;
    }

    if (isHybridApp()) {
      await FilesCubit.downloadFileNative(blob, filename);
    } else {
      triggerBlobDownload(blob, filename);
    }
  };

  static downloadFileNative = async (
    blob: Blob,
    filename: string
  ): Promise<void> => {
    try {
      const base64Data = await blobToBase64(blob);

      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents
      });

      const fileMimeType = blob.type || "application/octet-stream";

      toast.show("download.savedToLocation", {
        severity: "success",
        translationVariables: {
          location: isIOS()
            ? translate("download.location.ios")
            : translate("download.location.android")
        },
        buttons: [
          {
            text: translate("download.openFile"),
            handler: async () => {
              try {
                await FileOpener.openFile({
                  path: result.uri,
                  mimeType: fileMimeType
                });
              } catch (error) {
                reportErrorSentry(error, {
                  uri: result.uri,
                  mimeType: fileMimeType
                });

                if (
                  blob.type === PDF_MIME_TYPE ||
                  filename.toLowerCase().endsWith(".pdf")
                ) {
                  const blobUrl = URL.createObjectURL(blob);
                  PDFViewerController.open(blobUrl, filename, undefined);
                  return;
                }

                toast.error(ERROR_OPEN_FILE_FAILED);
              }
            }
          }
        ]
      });
    } catch (error) {
      reportErrorSentry(error);
      toast.error(ERROR_DOWNLOAD_FILE);
    }
  };

  static fileNameWithExtension = (
    filename: string,
    fileType: string
  ): string => {
    if (filename.includes(".")) {
      return filename;
    }
    return fileType ? `${filename}.${fileType.split("/")[1]}` : filename;
  };

  static shareFile = async (blob: Blob, filename: string): Promise<void> => {
    try {
      const file = new File(
        [blob],
        FilesCubit.fileNameWithExtension(filename, blob.type),
        { type: blob.type }
      );
      await navigator.share({
        files: [file],
        title: filename
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        // User cancelled
      } else {
        reportErrorSentry(error);
        toast.error("error.shareFile");
      }
    }
  };

  public readonly removeFile = (id: string): void => {
    this.emit(this.state.filter((file) => id !== file.id));
  };

  previewUrlCache: Record<string, string | undefined> = {};
  previewRequestCache: Record<string, Promise<string> | undefined> = {};
  public readonly loadPreview = async (
    id: string,
    w = 400,
    h = 400
  ): Promise<string> => {
    const requestId = `${id}-${w}-${h}`;

    const request = async (): Promise<string> => {
      try {
        const response = await FilesCubit.requestDownloadFilePreview(id, w, h);
        if (!response.ok) {
          throw new Error(`Failed to load preview for file ${id}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        this.previewUrlCache[requestId] = url;
        return url;
      } catch (e: unknown) {
        reportErrorSentry(e);
      }
      return "";
    };

    if (this.previewUrlCache[requestId]) {
      return this.previewUrlCache[requestId] ?? "";
    }

    if (this.previewRequestCache[requestId]) {
      return this.previewRequestCache[requestId] ?? "";
    }

    this.previewRequestCache[requestId] = request();
    return this.previewRequestCache[requestId] ?? "";
  };

  static openPDFNative = async (
    blob: Blob | undefined,
    filename: string,
    cachedUri?: string,
    context?: FileViewContext
  ): Promise<void> => {
    try {
      if (cachedUri) {
        await FileOpener.openFile({
          path: cachedUri,
          mimeType: PDF_MIME_TYPE
        });
        tracker.track(TrackEvent.FileViewer, {
          type: TrackType.complete,
          data: {
            fileId: context?.fileId,
            fileName: filename,
            fileType: context?.contentType ?? PDF_MIME_TYPE,
            fileSize: context?.contentLength,
            source: context?.source,
            wasCached: true,
            stage: "open-pdf"
          }
        });
        return;
      }

      if (!blob) {
        throw new Error("No blob or cached URI provided");
      }

      const base64Data = await blobToBase64(blob);

      const pdfFilename = filename.endsWith(".pdf")
        ? filename
        : `${filename}.pdf`;

      const result = await Filesystem.writeFile({
        path: pdfFilename,
        data: base64Data,
        directory: Directory.Cache
      });

      await FileOpener.openFile({
        path: result.uri,
        mimeType: PDF_MIME_TYPE
      });
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.complete,
        data: {
          fileId: context?.fileId,
          fileName: filename,
          fileType: context?.contentType ?? PDF_MIME_TYPE,
          fileSize: context?.contentLength,
          source: context?.source,
          wasCached: context?.wasCached ?? false,
          stage: "open-pdf"
        }
      });
    } catch (error) {
      reportErrorSentry(error, {
        fileName: filename,
        uri: cachedUri,
        mimeType: PDF_MIME_TYPE
      });
      tracker.track(TrackEvent.FileViewer, {
        type: TrackType.fail,
        data: {
          fileId: context?.fileId,
          fileName: filename,
          fileType: context?.contentType ?? PDF_MIME_TYPE,
          fileSize: context?.contentLength,
          source: context?.source,
          failurePoint: "open-native-pdf",
          errorMessage: error instanceof Error ? error.message : String(error)
        }
      });

      if (blob) {
        const blobUrl = URL.createObjectURL(blob);
        PDFViewerController.open(blobUrl, filename, context);
        return;
      }

      toast.error("error.openPdfFailed");
    }
  };
}
